/**
 * Shared field-level validators for the sign-up / profile / password
 * flows.
 *
 * Every validator returns `undefined` on OK and a user-facing error
 * message on failure. The page components track a per-field
 * `touched` bool, mark it `true` on blur, and pass `error &&
 * touched` to the corresponding MUI `TextField`'s `error` +
 * `helperText` props. That gets the "validate as soon as the user
 * clicks away" UX the operator asked for, without flashing errors
 * before the user has typed anything.
 *
 * All validators are total (no throws) and pure (no side effects),
 * so they compose freely into aggregate checks.
 */

import { matchIsValidTel } from 'mui-tel-input';

export const USERNAME_PATTERN = /^[a-zA-Z0-9_-]{3,30}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function usernameError(value: string): string | undefined {
  if (value.length === 0) return 'Username is required.';
  if (!USERNAME_PATTERN.test(value)) {
    return 'Username must be 3-30 characters (letters, digits, _ or -).';
  }
  return undefined;
}

export function firstNameError(value: string): string | undefined {
  if (value.trim().length === 0) return 'First name is required.';
  if (value.length > 60) return 'First name must be 60 characters or fewer.';
  return undefined;
}

export function lastNameError(value: string): string | undefined {
  if (value.trim().length === 0) return 'Last name is required.';
  if (value.length > 60) return 'Last name must be 60 characters or fewer.';
  return undefined;
}

export function emailError(value: string): string | undefined {
  if (value.length === 0) return 'Email is required.';
  if (!EMAIL_PATTERN.test(value)) return 'Enter a valid email address.';
  return undefined;
}

/**
 * Strip formatting from an `mui-tel-input` display value into strict
 * E.164 (`+CountryCodeDigits`, no spaces, no dashes). Cognito's
 * `phone_number` attribute + our backend `phoneE164` regex both
 * disallow whitespace, so every phone value that leaves the SPA for
 * Cognito or the profile-upsert API has to run through this first.
 * Empty input returns empty string.
 */
export function normalizePhoneE164(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  // Keep the leading `+` (E.164 requires it) and every digit; drop
  // everything else (spaces, dashes, parens, etc.).
  const cleaned = trimmed.replace(/[^0-9+]/g, '');
  if (!cleaned.startsWith('+')) return `+${cleaned.replace(/\+/g, '')}`;
  return `+${cleaned.slice(1).replace(/\+/g, '')}`;
}

/**
 * Phone validation. Empty input is OK (optional). Otherwise defers
 * to `matchIsValidTel` which wraps libphonenumber-js. `mui-tel-input`
 * emits formatted strings like `"+1 408 555 1234"`; `matchIsValidTel`
 * handles the formatting.
 */
export function phoneError(value: string): string | undefined {
  if (value.length === 0) return undefined;
  // MuiTelInput with `forceCallingCode` may leave the field as just
  // the calling code (e.g. `+1`) when the user has not typed
  // anything. Treat that as empty rather than invalid so the "click
  // into the field, click out" case does not flash a red error.
  const digitsOnly = value.replace(/[^0-9]/g, '');
  const callingCodeMatch = /^\+([1-9][0-9]{0,2})\s*$/.test(value.trim());
  if (callingCodeMatch || digitsOnly.length <= 3) return undefined;
  if (!matchIsValidTel(value)) return 'Enter a valid phone number for the selected country.';
  return undefined;
}

/**
 * Date-of-birth validation. Optional. Rejects future dates and
 * malformed inputs.
 */
export function dateOfBirthError(value: string): string | undefined {
  if (value.length === 0) return undefined;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Enter a valid date of birth.';
  if (parsed > new Date()) return 'Date of birth cannot be in the future.';
  const earliest = new Date('1900-01-01');
  if (parsed < earliest) return 'Enter a valid date of birth.';
  return undefined;
}

export interface PasswordRuleStatus {
  readonly id: 'length' | 'upper' | 'lower' | 'digit' | 'symbol';
  readonly label: string;
  readonly met: boolean;
}

/** Return the pass/fail status of every password policy rule. */
export function passwordRules(value: string): ReadonlyArray<PasswordRuleStatus> {
  return [
    { id: 'length', label: 'At least 12 characters', met: value.length >= 12 },
    { id: 'upper', label: 'Contains an uppercase letter', met: /[A-Z]/.test(value) },
    { id: 'lower', label: 'Contains a lowercase letter', met: /[a-z]/.test(value) },
    { id: 'digit', label: 'Contains a digit', met: /[0-9]/.test(value) },
    { id: 'symbol', label: 'Contains a symbol (! @ # $ etc.)', met: /[^A-Za-z0-9]/.test(value) },
  ];
}

/** Aggregate password error string, or undefined when every rule is met. */
export function passwordError(value: string): string | undefined {
  if (value.length === 0) return 'Password is required.';
  const failed = passwordRules(value).filter((r) => !r.met);
  if (failed.length === 0) return undefined;
  return 'Password does not meet every requirement below.';
}

export function passwordConfirmError(pw: string, confirm: string): string | undefined {
  if (confirm.length === 0) return 'Please confirm the password.';
  if (pw !== confirm) return 'Passwords do not match.';
  return undefined;
}
